/**
 * Batch Execution Panel
 * UI for planning and monitoring parallel job execution
 */

import React, { useState, useEffect } from 'react';
import { X, Play, Pause, Square, Clock, Zap, DollarSign, CheckCircle, AlertCircle, Loader2, RefreshCw, Languages, GitCompare, ClipboardCheck, Circle } from 'lucide-react';
import { useBatchSchedulerStore } from '../model';
import { getRateLimitCapacity } from '../scheduler';
import type { RateLimitCapacity } from '../../../services/shared/rateLimiter';
import type { ScheduledJob } from '../types';

interface BatchExecutionPanelProps {
    onClose: () => void;
    onExecute: () => void;
    onPause: () => void;
    onResume: () => void;
    onCancel: () => void;
}

// Progress bar component
const ProgressBar: React.FC<{ value: number; max: number; color?: string }> = ({
    value,
    max,
    color = 'bg-violet-500'
}) => {
    const percentage = max > 0 ? Math.min(100, (value / max) * 100) : 0;
    return (
        <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
            <div
                className={`h-full ${color} transition-all duration-300`}
                style={{ width: `${percentage}%` }}
            />
        </div>
    );
};

// Rate limit display (polls every 2s for live updates)
const RateLimitDisplay: React.FC = () => {
    const [capacity, setCapacity] = useState<RateLimitCapacity>(getRateLimitCapacity());
    const planStatus = useBatchSchedulerStore(s => s.currentPlan?.status);

    useEffect(() => {
        // Poll while plan is running or paused
        if (planStatus === 'running' || planStatus === 'paused') {
            const interval = setInterval(() => {
                setCapacity(getRateLimitCapacity());
            }, 2000);
            return () => clearInterval(interval);
        }
        // One final update when done
        setCapacity(getRateLimitCapacity());
    }, [planStatus]);

    return (
        <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
            <div className="text-xs font-bold text-slate-500 uppercase mb-2">Rate Limits</div>
            <div className="grid grid-cols-3 gap-3 text-xs">
                <div>
                    <div className="flex justify-between mb-1">
                        <span className="text-slate-600">RPM</span>
                        <span className="font-mono">{capacity.rpm.used}/{capacity.rpm.limit}</span>
                    </div>
                    <ProgressBar
                        value={capacity.rpm.used}
                        max={capacity.rpm.limit}
                        color={capacity.rpm.used > capacity.rpm.limit * 0.8 ? 'bg-amber-500' : 'bg-emerald-500'}
                    />
                </div>
                <div>
                    <div className="flex justify-between mb-1">
                        <span className="text-slate-600">TPM</span>
                        <span className="font-mono">{Math.round(capacity.tpm.used / 1000)}K/{Math.round(capacity.tpm.limit / 1000)}K</span>
                    </div>
                    <ProgressBar
                        value={capacity.tpm.used}
                        max={capacity.tpm.limit}
                        color={capacity.tpm.used > capacity.tpm.limit * 0.8 ? 'bg-amber-500' : 'bg-emerald-500'}
                    />
                </div>
                <div>
                    <div className="flex justify-between mb-1">
                        <span className="text-slate-600">RPD</span>
                        <span className="font-mono">{capacity.rpd.used}/{capacity.rpd.limit}</span>
                    </div>
                    <ProgressBar
                        value={capacity.rpd.used}
                        max={capacity.rpd.limit}
                        color={capacity.rpd.used > capacity.rpd.limit * 0.8 ? 'bg-amber-500' : 'bg-emerald-500'}
                    />
                </div>
            </div>
        </div>
    );
};

// Job status icon-only (compact but visible)
const JobStatusIcon: React.FC<{ status: ScheduledJob['status'] }> = ({ status }) => {
    const config = {
        pending: { color: 'text-slate-400', bg: 'bg-slate-100', icon: <Circle className="w-4 h-4" /> },
        running: { color: 'text-violet-600', bg: 'bg-violet-100', icon: <Loader2 className="w-4 h-4 animate-spin" /> },
        completed: { color: 'text-emerald-600', bg: 'bg-emerald-100', icon: <CheckCircle className="w-4 h-4" /> },
        failed: { color: 'text-red-600', bg: 'bg-red-100', icon: <AlertCircle className="w-4 h-4" /> },
        cancelled: { color: 'text-slate-400', bg: 'bg-slate-100', icon: <Square className="w-4 h-4" /> }
    };

    const { color, bg, icon } = config[status];

    return (
        <span className={`inline-flex items-center justify-center w-6 h-6 rounded ${bg} ${color}`} title={status.charAt(0).toUpperCase() + status.slice(1)}>
            {icon}
        </span>
    );
};

// Step indicator (job type icon + status) with colored badge
const StepIndicator: React.FC<{ job: ScheduledJob | undefined; icon: React.ReactNode; label: string; color: string }> = ({ job, icon, label, color }) => {
    if (!job) return null;
    return (
        <div className="flex items-center gap-1" title={`${label}: ${job.status}`}>
            <span className={`inline-flex items-center justify-center w-6 h-6 rounded ${color}`}>{icon}</span>
            <JobStatusIcon status={job.status} />
        </div>
    );
};

// Batch item (Grouped by file for display)
const FileJobGroup: React.FC<{ fileName: string; jobs: ScheduledJob[] }> = ({ fileName, jobs }) => {
    const isFileRunning = jobs.some(j => j.status === 'running');
    const totalFileTokens = jobs.reduce((s, j) => s + j.estimatedTokens, 0);

    // Find specific job types
    const transJob = jobs.find(j => j.type === 'translation');
    const matchJob = jobs.find(j => j.type === 'semantic-match');
    const auditJob = jobs.find(j => j.type === 'audit');

    return (
        <div className="bg-white rounded p-3 text-xs border border-slate-200 shadow-sm mb-2">
            <div className="flex items-center justify-between">
                <span className={`font-bold truncate flex-1 ${isFileRunning ? 'text-violet-700' : 'text-slate-700'}`}>
                    {fileName}
                </span>
                <div className="flex items-center gap-3 ml-2">
                    {/* Compact step indicators: icon + status icon with colors */}
                    <div className="flex items-center gap-1.5">
                        <StepIndicator job={transJob} icon={<Languages className="w-4 h-4" />} label="Translation" color="bg-blue-100 text-blue-600" />
                        <StepIndicator job={matchJob} icon={<GitCompare className="w-4 h-4" />} label="Semantic Match" color="bg-amber-100 text-amber-600" />
                        <StepIndicator job={auditJob} icon={<ClipboardCheck className="w-4 h-4" />} label="Audit" color="bg-purple-100 text-purple-600" />
                    </div>
                    <span className="text-[10px] text-slate-400 font-mono">
                        ~{Math.round(totalFileTokens / 1000)}k
                    </span>
                </div>
            </div>
        </div>
    );
};

// Main panel component
const BatchExecutionPanel: React.FC<BatchExecutionPanelProps> = ({
    onClose,
    onExecute,
    onPause,
    onResume,
    onCancel
}) => {
    const { currentPlan, progress, isPaused, isPlanning, config, setConfig } = useBatchSchedulerStore();

    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}m ${secs}s`;
    };

    const isRunning = currentPlan?.status === 'running';
    const isCompleted = currentPlan?.status === 'completed';
    const isCancelled = currentPlan?.status === 'cancelled';

    // Group jobs by file for display
    const fileGroups = React.useMemo(() => {
        if (!currentPlan) return {};
        const groups: Record<string, ScheduledJob[]> = {};
        currentPlan.jobs.forEach(job => {
            if (!groups[job.fileName]) {
                groups[job.fileName] = [];
            }
            groups[job.fileName].push(job);
        });
        return groups;
    }, [currentPlan]);

    const fileNames = Object.keys(fileGroups);

    return (
        <div className="fixed inset-y-0 right-0 w-96 bg-white shadow-2xl border-l border-slate-200 flex flex-col z-50">
            {/* Header */}
            <div className="h-14 border-b border-slate-200 flex items-center justify-between px-4 shrink-0">
                <div className="flex items-center gap-2">
                    <Zap className="w-5 h-5 text-violet-500" />
                    <h2 className="font-bold text-slate-800">Batch Execution</h2>
                </div>
                <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
                    <X className="w-5 h-5" />
                </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {/* Rate Limits */}
                <RateLimitDisplay />

                {/* Plan Summary */}
                {currentPlan && (
                    <div className="bg-white rounded-lg border border-slate-200 p-4">
                        <div className="text-xs font-bold text-slate-500 uppercase mb-3">Execution Plan</div>

                        <div className="grid grid-cols-2 gap-3 mb-4">
                            <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center">
                                    <Play className="w-4 h-4 text-violet-600" />
                                </div>
                                <div>
                                    <div className="text-lg font-bold text-slate-800">{currentPlan.totalFiles}</div>
                                    <div className="text-[10px] text-slate-500 uppercase">Files</div>
                                </div>
                            </div>

                            <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                                    <Clock className="w-4 h-4 text-amber-600" />
                                </div>
                                <div>
                                    <div className="text-lg font-bold text-slate-800">{formatTime(currentPlan.estimatedTime)}</div>
                                    <div className="text-[10px] text-slate-500 uppercase">Est. Time</div>
                                </div>
                            </div>

                            <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                                    <Zap className="w-4 h-4 text-emerald-600" />
                                </div>
                                <div>
                                    <div className="text-lg font-bold text-slate-800">{Math.round(currentPlan.estimatedTokens / 1000)}K</div>
                                    <div className="text-[10px] text-slate-500 uppercase">Tokens</div>
                                </div>
                            </div>

                            <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                                    <DollarSign className="w-4 h-4 text-blue-600" />
                                </div>
                                <div>
                                    <div className="text-lg font-bold text-slate-800">${currentPlan.estimatedCost}</div>
                                    <div className="text-[10px] text-slate-500 uppercase">Est. Cost</div>
                                </div>
                            </div>
                        </div>

                        {/* Progress */}
                        {progress && isRunning && (
                            <div className="mb-4">
                                <div className="flex justify-between text-xs text-slate-600 mb-1">
                                    <span>Overall Progress</span>
                                    <span>{progress.completedJobs}/{progress.totalJobs} jobs</span>
                                </div>
                                <ProgressBar
                                    value={progress.completedJobs}
                                    max={progress.totalJobs}
                                    color="bg-violet-500"
                                />
                                {progress.runningJobs > 0 && (
                                    <div className="text-[10px] text-violet-600 text-center mt-1 animate-pulse">
                                        ⚡ {progress.runningJobs} concurrent jobs running...
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Files List */}
                        <div className="space-y-2">
                            {/* Show active/running files first, then pending */}
                            {fileNames.sort((a, b) => {
                                const aRunning = fileGroups[a].some(j => j.status === 'running');
                                const bRunning = fileGroups[b].some(j => j.status === 'running');
                                if (aRunning && !bRunning) return -1;
                                if (!aRunning && bRunning) return 1;
                                return 0;
                            }).map(fileName => (
                                <FileJobGroup
                                    key={fileName}
                                    fileName={fileName}
                                    jobs={fileGroups[fileName]}
                                />
                            ))}
                        </div>
                    </div>
                )}

                {/* Loading state */}
                {isPlanning && (
                    <div className="flex flex-col items-center justify-center py-10">
                        <Loader2 className="w-8 h-8 text-violet-500 animate-spin mb-3" />
                        <p className="text-slate-500">Creating execution plan...</p>
                    </div>
                )}

                {/* No plan */}
                {!currentPlan && !isPlanning && (
                    <div className="text-center py-10 text-slate-400">
                        <Zap className="w-12 h-12 mx-auto mb-3 opacity-30" />
                        <p>Select files and click "Run Batch" to create an execution plan</p>
                    </div>
                )}
            </div>

            {/* Footer Actions */}
            <div className="border-t border-slate-200 flex flex-col px-4 py-3 shrink-0 bg-slate-50">
                {!isRunning && !isCompleted && currentPlan && (
                    <>
                        {/* Options Row */}
                        <div className="flex items-center gap-4 mb-3">
                            <label className="flex items-center gap-2 text-sm cursor-pointer" title="Retry failed jobs automatically">
                                <input
                                    type="checkbox"
                                    checked={config.autoRetry}
                                    onChange={(e) => setConfig({ autoRetry: e.target.checked })}
                                    className="w-4 h-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500"
                                />
                                <RefreshCw className="w-4 h-4 text-slate-500" />
                                <span className="text-slate-600">Auto-retry on error</span>
                            </label>
                        </div>

                        {/* Buttons Row */}
                        <div className="flex items-center justify-between">
                            <button
                                onClick={onCancel}
                                className="px-4 py-2 text-slate-600 hover:bg-slate-200 rounded-lg text-sm font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={onExecute}
                                className="px-6 py-2 bg-violet-600 hover:bg-violet-500 text-white rounded-lg font-bold flex items-center gap-2"
                            >
                                <Play className="w-4 h-4" />
                                Execute Plan
                            </button>
                        </div>
                    </>
                )}

                {/* Cancel/Stop button only visible when running */}
                {isRunning && (
                    <>
                        <button
                            onClick={onCancel}
                            className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg text-sm font-medium flex items-center gap-2"
                        >
                            <Square className="w-4 h-4" />
                            Stop
                        </button>
                        <button
                            onClick={isPaused ? onResume : onPause}
                            className={`px-6 py-2 rounded-lg font-bold flex items-center gap-2 ${isPaused
                                ? 'bg-emerald-600 hover:bg-emerald-500 text-white'
                                : 'bg-amber-500 hover:bg-amber-400 text-white'
                                }`}
                        >
                            {isPaused ? (
                                <>
                                    <Play className="w-4 h-4" />
                                    Resume
                                </>
                            ) : (
                                <>
                                    <Pause className="w-4 h-4" />
                                    Pause
                                </>
                            )}
                        </button>
                    </>
                )}

                {(isCompleted || isCancelled) && (
                    <button
                        onClick={onClose}
                        className="w-full px-6 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded-lg font-bold"
                    >
                        Close
                    </button>
                )}
            </div>
        </div>
    );
};

export default BatchExecutionPanel;
